import React from "react";
import { Link } from "@/components/LocaleLink";
import { motion } from "framer-motion";
import { CheckCircle2, ShieldCheck, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { getIndsatser } from "@/data/indsatser";
import { useSeo } from "@/hooks/useSeo";
import PageHero from "@/components/PageHero";
import IndsatsCard from "@/components/IndsatsCard";
import IndsatsCompare from "@/components/IndsatsCompare";
import IndsatsCTA from "@/components/IndsatsCTA";
import FinalCTA from "@/components/FinalCTA";
import { Reveal, Eyebrow } from "@/components/Reveal";

const IMG = "https://images.unsplash.com/photo-1765366417046-f46361a7f26f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const Indsatser = () => {
  const { lang } = useLang();
  const d = getIndsatser(lang);
  useSeo(
    lang === "da"
      ? "Indsatser og forløb | Lotusgruppen"
      : "Programmes & pathways | Lotusgruppen",
    d.overview.intro
  );

  return (
    <>
      <PageHero eyebrow={d.overview.eyebrow} title={d.overview.title} intro={d.overview.intro} image={IMG} />

      {/* Product grid */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24" data-testid="indsatser-grid">
        <div className="mb-10">
          <Link
            to="/find-forloeb"
            className="group inline-flex items-center gap-2 rounded-full bg-[#C47C5D] text-white px-6 py-3 font-medium hover:bg-[#b06a4c] transition-colors duration-300"
            data-testid="indsatser-find-forloeb"
          >
            {lang === "da" ? "Find det rette forløb" : "Find the right pathway"}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/kommuner-og-kriminalforsorgen"
            className="group inline-flex items-center gap-2 rounded-full border border-[#2D5A3F] text-[#2D5A3F] px-6 py-3 font-medium hover:bg-[#2D5A3F] hover:text-white transition-colors duration-300 ml-0 sm:ml-3 mt-3 sm:mt-0"
            data-testid="indsatser-kommuner-link"
          >
            {lang === "da" ? "Kommuner og Kriminalforsorgen" : "Municipalities & the Probation Service"}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {d.products.map((p, i) => (
            <IndsatsCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* Comparison */}
      <IndsatsCompare />

      {/* Audience */}
      <section className="bg-[#F7F5F0] border-y border-black/5" data-testid="indsatser-audience">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 lg:py-24 grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-start">
          <div>
            <Reveal><Eyebrow>{d.overview.audienceTitle}</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <p className="mt-5 font-display text-2xl sm:text-3xl text-[#1A2E24] leading-snug">{d.overview.audienceIntro}</p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <ul className="grid sm:grid-cols-2 gap-3">
              {d.overview.audience.map((a) => (
                <li key={a} className="flex items-center gap-3 rounded-2xl bg-white border border-black/5 px-5 py-4 text-[#3B5245]">
                  <CheckCircle2 className="h-5 w-5 text-[#8EA694] shrink-0" /> {a}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Guarantees */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20 lg:py-24" data-testid="indsatser-guarantees">
        <div className="max-w-2xl mb-12">
          <Reveal><Eyebrow>{d.overview.guaranteesTitle}</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <p className="mt-5 text-lg text-[#3B5245] leading-relaxed">{d.overview.guaranteesIntro}</p>
          </Reveal>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {d.overview.guarantees.map((g, i) => (
            <motion.div
              key={g}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className="flex items-start gap-4 rounded-2xl bg-[#EAE5D9] p-6"
            >
              <ShieldCheck className="h-6 w-6 text-[#2D5A3F] shrink-0" />
              <span className="text-[#1A2E24] font-medium leading-snug">{g}</span>
            </motion.div>
          ))}
        </div>
      </section>

      <IndsatsCTA dark />
      <FinalCTA />
    </>
  );
};

export default Indsatser;
